import { useQueryClient } from "@tanstack/react-query";
import { currentUser, signOut } from "actions/helpers/auth";
import PageSpinner from "components/Spinners/PageSpinner";
import { primeAndScopeProfile } from "hooks/useProfile";
import { clearPersistedCache } from "lib/queryCachePersist";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "reducers/auth";

/**
 * Resolves the session ONCE, above the router. Mounting this before the routes
 * means a cold load of a deep `/admin/*` URL restores the session in place
 * instead of bouncing through `/auth/login` (which is what happened when session
 * restore was a side-effect of the Login route mounting). Renders a single
 * spinner until the session resolves, then the app — so the router never
 * evaluates an admin route while still unauthenticated.
 */
export default function AuthBootstrap({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { state, dispatch } = useContext(AuthContext);
  const queryClient = useQueryClient();
  // One-time flag that flips true after the initial session resolves.
  const [bootstrapped, setBootstrapped] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: restore the session exactly once on mount
  useEffect(() => {
    dispatch({ type: "AUTHENTICATING" });
    currentUser()
      .then(() =>
        // Prime the profile and drop any other user's hydrated snapshot before
        // the app is revealed.
        primeAndScopeProfile(queryClient)
          .then(() => dispatch({ type: "AUTHENTICATING_SUCCESS" }))
          .catch((err) => {
            // Session is valid but the profile load failed — force a clean
            // sign-out, clearing the cache like a normal logout.
            clearPersistedCache();
            signOut().finally(() =>
              dispatch({ type: "AUTHENTICATING_ERROR", payload: err }),
            );
          }),
      )
      .catch(() => dispatch({ type: "AUTHENTICATING_ERROR", payload: null }))
      .finally(() => setBootstrapped(true));
  }, []);

  // Reveal children either when the auth state settles (`!authenticating`) OR once
  // the initial bootstrap is done. The first condition is atomic with
  // `authenticated` flipping true (same AUTHENTICATING_SUCCESS action), so a
  // successful silent auth goes straight to /admin with no /auth/login flash; the
  // second keeps the app mounted when a later sign-in re-raises `authenticating`,
  // so the login form isn't unmounted/cleared mid-submit.
  const ready = !state.authenticating || bootstrapped;
  return <PageSpinner spin={!ready}>{ready && children}</PageSpinner>;
}
